/**
 * Shared vocabulary for the Compilations feature — status meta, badges and
 * formatters, in one place so the card, the list row and every dialog agree
 * on what a status looks like and how a size or date is written.
 */
import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { formatDistanceToNowStrict } from "date-fns";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import { ApprovalStatus } from "../../Enums/enums";

export const APPROVAL_META = {
  [ApprovalStatus.PENDING_APPROVAL]: {
    label: "Awaiting approval",
    short: "Pending",
    color: "#b45309",
    bg: "#fffbeb",
    border: "#fde68a",
    icon: HourglassEmptyIcon,
  },
  [ApprovalStatus.APPROVED]: {
    label: "Approved · on S3",
    short: "Approved",
    color: "#15803d",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    icon: CloudDoneIcon,
  },
  [ApprovalStatus.REJECTED]: {
    label: "Rejected",
    short: "Rejected",
    color: "#b91c1c",
    bg: "#fef2f2",
    border: "#fecaca",
    icon: ThumbDownAltIcon,
  },
};

export const metaFor = (status) => APPROVAL_META[status] || APPROVAL_META[ApprovalStatus.PENDING_APPROVAL];

export const SORT_OPTIONS = [
  { value: "NEWEST", label: "Newest first" },
  { value: "OLDEST", label: "Oldest first" },
  { value: "TITLE", label: "Title A–Z" },
  { value: "WGIDS", label: "Most WGIDs" },
];

export const formatBytes = (bytes) => {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

/** "4.2 MB/s" — the unit people expect for a transfer rate. */
export const formatRate = (bytesPerSecond) => {
  if (!bytesPerSecond || bytesPerSecond <= 0) return "—";
  return `${formatBytes(bytesPerSecond)}/s`;
};

/** "3m 20s left" — coarse on purpose; a to-the-second ETA would be a lie. */
export const formatEta = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return `${Math.ceil(seconds)}s left`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return `${mins}m ${secs}s left`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m left`;
};

const parseDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const relativeTime = (iso) => {
  const d = parseDate(iso);
  return d ? `${formatDistanceToNowStrict(d)} ago` : "—";
};

export const absoluteTime = (iso) => {
  const d = parseDate(iso);
  return d ? d.toLocaleString() : "Unknown date";
};

/** Relative up front, exact on hover — the same treatment used in Approvals. */
export function TimeAgo({ iso, prefix = "", variant = "caption", sx }) {
  if (!iso) return null;
  return (
    <Tooltip title={absoluteTime(iso)} placement="top">
      <Typography
        component="span"
        variant={variant}
        sx={{ cursor: "help", color: "text.secondary", fontVariantNumeric: "tabular-nums", ...sx }}
      >
        {prefix}
        {relativeTime(iso)}
      </Typography>
    </Tooltip>
  );
}

/** The WCA id, always monospaced so ids line up wherever they're listed. */
export function WcaBadge({ children, size = "medium", tone = "success" }) {
  const small = size === "small";
  const tones = {
    success: { color: "#166534", bg: "#f0fdf4", border: "#bbf7d0" },
    light: { color: "#fff", bg: "rgba(2,6,23,.55)", border: "rgba(255,255,255,.25)" },
  };
  const t = tones[tone] || tones.success;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "'Roboto Mono', ui-monospace, monospace",
        fontWeight: 700,
        letterSpacing: 0.4,
        fontSize: small ? 10.5 : 12,
        lineHeight: 1,
        px: small ? 0.8 : 1.1,
        py: small ? 0.5 : 0.65,
        borderRadius: 1.5,
        color: t.color,
        bgcolor: t.bg,
        border: `1px solid ${t.border}`,
        backdropFilter: tone === "light" ? "blur(4px)" : undefined,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

export function ApprovalBadge({ status, size = "small", full = true }) {
  const meta = metaFor(status);
  const Icon = meta.icon;
  return (
    <Chip
      size={size}
      icon={<Icon sx={{ fontSize: 13, color: `${meta.color} !important` }} />}
      label={full ? meta.label : meta.short}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 600,
        color: meta.color,
        bgcolor: meta.bg,
        border: `1px solid ${meta.border}`,
        "& .MuiChip-label": { px: 0.8 },
      }}
    />
  );
}
